import { Button, Card, notification, Skeleton } from "antd";
import { DownloadOutlined, IdcardOutlined } from "@ant-design/icons";
import React, { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { downloadCSV, formatDate, getUserRole } from "../../services/util";
import CustomHeader from "../common/Header";
import CustomTable from "../common/Table";
import { DashboardGrids } from "../Home/Home";
import "./Details.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const DOWNLOAD_ROLES = ["Super Admin", "Admin", "Management"];
const SCAN_ROLES = ["Super Admin", "Admin", "Support"];

/**
 * Full column definitions. The rendered table always filters to just
 * "hotel_name" + the active `type` column, so only one data column
 * is ever shown alongside the hotel name.
 */
const buildColumns = ({
    type,
    sortedInfo,
    onHotelClick,
    onTransactionClick,
}) => {
    const sortOrder = (key) =>
        sortedInfo.columnKey === key ? sortedInfo.order : null;

    const HotelNameCell = (text, record) =>
        type === "transaction_count" ? (
            <button
                className="details-link-btn"
                onClick={() => onTransactionClick(record.hotel_id)}
            >
                {text}
            </button>
        ) : (
            <span
                className={
                    record.calls_count ? "details-link" : "details-plain"
                }
                onClick={() =>
                    record.calls_count && onHotelClick(record.hotel_id)
                }
            >
                {text}
            </span>
        );

    return [
        {
            title: "Hotel Name",
            dataIndex: "hotel_name",
            key: "hotel_name",
            fixed: "left",
            sorter: () => {},
            sortOrder: sortOrder("hotel_name"),
            render: HotelNameCell,
        },
        {
            title: "Total Reservations",
            dataIndex: "checkin_count",
            key: "checkin_count",
            sorter: () => {},
            sortOrder: sortOrder("checkin_count"),
        },
        {
            title: "Check-Ins",
            dataIndex: "total_checkin_new",
            key: "total_checkin_new",
            sorter: () => {},
            sortOrder: sortOrder("total_checkin_new"),
        },
        {
            title: "Walk-Ins",
            dataIndex: "walkin_count",
            key: "walkin_count",
            sorter: () => {},
            sortOrder: sortOrder("walkin_count"),
        },
        {
            title: "Scan",
            dataIndex: "scan_count",
            key: "scan_count",
            sorter: () => {},
            sortOrder: sortOrder("scan_count"),
        },
        {
            title: "Cash Collected ($)",
            dataIndex: "cash_collected",
            key: "cash_collected",
            sorter: () => {},
            sortOrder: sortOrder("cash_collected"),
        },
        {
            title: "Cash Collected Transactions",
            dataIndex: "cash_collected_trans",
            key: "cash_collected_trans",
            sorter: () => {},
            sortOrder: sortOrder("cash_collected_trans"),
        },
        {
            title: "Cash Dispensed ($)",
            dataIndex: "cash_dispence",
            key: "cash_dispence",
            sorter: () => {},
            sortOrder: sortOrder("cash_dispence"),
        },
        {
            title: "Cash Dispensed Transactions",
            dataIndex: "cash_dispenced_trans",
            key: "cash_dispenced_trans",
            sorter: () => {},
            sortOrder: sortOrder("cash_dispenced_trans"),
        },
        {
            title: "Keys Dispensed",
            dataIndex: "keys_count",
            key: "keys_count",
            sorter: () => {},
            sortOrder: sortOrder("keys_count"),
        },
        {
            title: "Calls",
            dataIndex: "calls_count",
            key: "calls_count",
            sorter: () => {},
            sortOrder: sortOrder("calls_count"),
        },
        {
            title: "Transactions",
            dataIndex: "transaction_count",
            key: "transaction_count",
            sorter: () => {},
            sortOrder: sortOrder("transaction_count"),
        },
        {
            title: "Cash ($)",
            dataIndex: "total_cash",
            key: "total_cash",
            sorter: () => {},
            sortOrder: sortOrder("total_cash"),
        },
        {
            title: "Housekeeping Tickets",
            dataIndex: "hk_ticket_count",
            key: "hk_ticket_count",
            sorter: () => {},
            sortOrder: sortOrder("hk_ticket_count"),
        },
        {
            title: "Housekeeping Orders",
            dataIndex: "hk_order_count",
            key: "hk_order_count",
            sorter: () => {},
            sortOrder: sortOrder("hk_order_count"),
        },
        {
            title: "Maintenance Tickets",
            dataIndex: "mnt_ticket_count",
            key: "mnt_ticket_count",
            sorter: () => {},
            sortOrder: sortOrder("mnt_ticket_count"),
        },
        {
            title: "Dining Orders",
            dataIndex: "dinning_order_count",
            key: "dinning_order_count",
            sorter: () => {},
            sortOrder: sortOrder("dinning_order_count"),
        },
        {
            title: "Check-Outs",
            dataIndex: "checkout_keys_count",
            key: "checkout_keys_count",
            sorter: () => {},
            sortOrder: sortOrder("checkout_keys_count"),
        },
        {
            title: "Enrolled Count",
            dataIndex: "enrolled_count",
            key: "enrolled_count",
            sorter: () => {},
            sortOrder: sortOrder("enrolled_count"),
        },
        {
            title: "Self Check-In Count",
            dataIndex: "self_checkin_count",
            key: "self_checkin_count",
            sorter: () => {},
            sortOrder: sortOrder("self_checkin_count"),
        },
    ];
};

// ─── Component ────────────────────────────────────────────────────────────────

function Details({ isModel = false, fieldKey = "" }) {
    const params = useParams();
    const type = isModel ? fieldKey : params.type;
    const location = useLocation();
    const navigate = useNavigate();
    const userRole = getUserRole();

    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const hotel_assigned = localStorage.getItem("hotel_assigned");
    const [api, contextHolder] = notification.useNotification();
    const {
        selectedDates,
        selectedHotels,
        selectedHotelType,
        selectedHotelActiveType,
        kioskSelection,
        selectedCallCenters,
        selectedHotelGroup,
        selectedServiceModel,
    } = useContext(SettingContext);

    // ── State ─────────────────────────────────────────────────────────────────

    const [hotelData, setHotelData] = useState([]);
    const [hotelHasData, setHotelHasData] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortedInfo, updateSortedInfo] = useState({
        order: "descend",
        columnKey: type,
    });

    const [start_date, end_date] = selectedDates;

    const [formData, setFormData] = useState({
        data_for: type,
        hotel_ids: selectedHotels,
        all_selected: selectedHotels.length === 0,
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
        page: 0,
        limit: 10,
        hotel_type: selectedHotelType,
        hotel_active_type: selectedHotelActiveType,
        ...(selectedHotelType === "kiosk"
            ? { calling_hotel_type: kioskSelection }
            : {}),
        staff_id,
        sort_by: type,
        sorting: 1, // descend = 1
        call_centers: selectedCallCenters,
        group_ids: selectedHotelGroup.includes("all") ? [] : selectedHotelGroup,
        service_model: selectedServiceModel.includes("all")
            ? []
            : selectedServiceModel,
    });

    // ── Query ─────────────────────────────────────────────────────────────────

    const {
        status: detailStatus,
        data: detailRes,
        refetch: fetchDetails,
    } = useQuery(
        type,
        () => HTTP("/dashboard/getDashboardData", { ...formData }),
        { enabled: false },
    );

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        fetchDetails();
    }, []);
    useEffect(() => {
        fetchDetails();
    }, [formData]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            hotel_type: selectedHotelType,
            ...(selectedHotelType === "kiosk"
                ? { calling_hotel_type: kioskSelection }
                : {}),
            hotel_ids: [],
            all_selected: true,
            page: 0,
        }));
        setCurrentPage(1);
    }, [selectedHotelType]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            call_centers: selectedCallCenters,
        }));
    }, [selectedCallCenters]);

    useEffect(() => {
        setFormData((previous) => ({
            ...previous,
            group_ids: selectedHotelGroup.includes("all")
                ? []
                : selectedHotelGroup,
        }));
    }, [selectedHotelGroup]);

    useEffect(() => {
        setFormData((previous) => ({
            ...previous,
            service_model: selectedServiceModel.includes("all")
                ? []
                : selectedServiceModel,
        }));
    }, [selectedServiceModel]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            hotel_active_type: selectedHotelActiveType,
            hotel_ids: [],
            all_selected: true,
            page: 0,
        }));
        setCurrentPage(1);
    }, [selectedHotelActiveType]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            calling_hotel_type: kioskSelection,
        }));
    }, [kioskSelection]);

    useEffect(() => {
        const [s, e] = selectedDates;
        setFormData((prev) => ({
            ...prev,
            start_date: formatDate(s),
            end_date: formatDate(e),
        }));
    }, [selectedDates]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            hotel_ids: selectedHotels,
            page: 0,
            all_selected: selectedHotels.length === 0,
        }));
    }, [selectedHotels]);

    useEffect(() => {
        if (detailStatus === "success") {
            const { data, total } = detailRes;
            if (data.length) setHotelHasData(true);
            setHotelData(data);
            setTotalRecords(total);
        }
    }, [detailStatus, detailRes]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleTableChange = (pagination, _, sorter) => {
        const { current, pageSize } = pagination;
        const { order, columnKey } = sorter;
        const updates = { page: current - 1, limit: pageSize };

        if (order) {
            updates.sorting = order === "ascend" ? 2 : 1;
            updates.sort_by = columnKey;
            updateSortedInfo({ columnKey, order });
        } else {
            const { sorting: _s, sort_by: _sb, ...rest } = formData;
            updateSortedInfo({});
            return setFormData({ ...rest, ...updates });
        }

        setFormData((prev) => ({ ...prev, ...updates }));
        setCurrentPage(current);
    };

    const downloadReport = async () => {
        if (hotelData.length === 0) {
            api["info"]({
                message: <strong>No data available</strong>,
                description:
                    "There is no data available to download for the selected filters.",
            });
            return;
        }
        const { page: _p, limit: _l, ...payload } = formData;
        const res = await HTTP("/dashboard/getDashboardDataReport", payload);
        const field = DashboardGrids.find(({ key }) => key === type);
        downloadCSV(res, `${field?.title ?? type}_reports`);
    };

    // ── Derived ───────────────────────────────────────────────────────────────

    const isScanPage = location.pathname.split("/").includes("scan_count");
    const canDownload = DOWNLOAD_ROLES.includes(userRole);
    const canScanRetrieve = SCAN_ROLES.includes(userRole);
    const isExternalVendorNoHotel =
        userRole === "External Vendor" && hotel_assigned === "false";

    const visibleColumns = buildColumns({
        type,
        sortedInfo,
        onHotelClick: (id) => navigate(`/calls/${id}`),
        onTransactionClick: (id) => navigate(`/transactions/${id}`),
    }).filter(({ key }) => key === "hotel_name" || key === type);

    const tableProps = {
        columns: visibleColumns,
        tableData: hotelData,
        currentPage,
        noOfrecords: totalRecords,
        handleTableChange,
        rowKey: "hotel_id",
        className: "dashboard-table",
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Card className="dashboard-container details-page">
            {contextHolder}
            {!isModel && (
                <CustomHeader
                    extraAfter={
                        <div style={{ display: "flex", gap: 8 }}>
                            {canDownload && (
                                <Button
                                    type="primary"
                                    icon={<DownloadOutlined />}
                                    onClick={downloadReport}
                                >
                                    Download
                                </Button>
                            )}
                            {canScanRetrieve && isScanPage && (
                                <Button
                                    type="primary"
                                    icon={<IdcardOutlined />}
                                    onClick={() =>
                                        navigate("/id-scanned-audit")
                                    }
                                >
                                    ID Retrieval
                                </Button>
                            )}
                        </div>
                    }
                />
            )}

            {detailStatus === "loading" || detailStatus === "idle" ? (
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 20, width: 40 }}
                />
            ) : (
                <CustomTable {...tableProps} />
            )}
        </Card>
    );
}

export { Details };
